import { Controller } from '../utils/controller';
import Swiper, { Autoplay, Navigation, Pagination, Thumbs } from 'swiper';

export default class extends Controller {
  static targets = [
    'thumbnail',
    'pagination',
    'thumbnailLeftNav',
    'thumbnailRightNav',
    'leftNav',
    'rightNav',
    'slide'
  ];
  static requiredTargets = [];

  static values = {
    slidesPerViewThumbnail: { type: Number, default: 5 },
    slidesSpaceBetweenThumbnail: { type: Number, default: 10 },
    slidesAutoHeight: { type: Boolean, default: false },
    slidesAutoPlay: { type: Boolean, default: false },
    slidesPerViewXxl: { type: Number, default: 4 },
    slidesPerViewXl: { type: Number, default: 4 },
    slidesPerViewL: { type: Number, default: 3 },
    slidesPerViewM: { type: Number, default: 2 },
    slidesPerViewS: { type: Number, default: 1 },
    slidesPerViewXs: { type: Number, default: 1 },
    slidesPerGroupXl: { type: Number, default: 1 },
    slidesPerGroupL: { type: Number, default: 1 },
    slidesPerGroupM: { type: Number, default: 1 },
    slidesPerGroupS: { type: Number, default: 1 },
    slidesSpaceBetween: { type: Number, default: 10 },
    slidesLoop: { type: Boolean, default: false },
    slidesLoopDesktop: { type: String, default: 'default' },
    thumbsSlidesPerGroup: { type: Number, default: 1 },
    slidesTrackActive: { type: Boolean, default: false },
    slidesTrackActiveTarget: { type: String, default: '' },
    slidesStartAt: { type: Number, default: 0 }
  };

  // TARGETS
  declare thumbnailTarget: HTMLElement;
  declare thumbnailLeftNavTarget: HTMLElement;
  declare thumbnailRightNavTarget: HTMLElement;
  declare paginationTarget: HTMLElement;
  declare leftNavTarget: HTMLElement;
  declare rightNavTarget: HTMLElement;
  declare slideTarget: HTMLElement;
  declare readonly hasThumbnailTarget: boolean;
  declare readonly hasThumbnailLeftNavTarget: boolean;
  declare readonly hasThumbnailRightNavTarget: boolean;
  declare readonly hasPaginationTarget: boolean;
  declare readonly hasLeftNavTarget: boolean;
  declare readonly hasRightNavTarget: boolean;
  declare readonly hasSlideTarget: boolean;

  // VALUES
  declare slidesPerViewThumbnailValue: number;
  declare slidesSpaceBetweenThumbnailValue: number;
  declare slidesAutoHeightValue: boolean;
  declare slidesAutoPlayValue: boolean;
  declare slidesPerViewXxlValue: number;
  declare slidesPerViewXlValue: number;
  declare slidesPerViewLValue: number;
  declare slidesPerViewMValue: number;
  declare slidesPerViewSValue: number;
  declare slidesPerViewXsValue: number;
  declare slidesPerGroupXlValue: number;
  declare slidesPerGroupLValue: number;
  declare slidesPerGroupMValue: number;
  declare slidesPerGroupSValue: number;
  declare slidesSpaceBetweenValue: number;
  declare slidesLoopValue: boolean;
  declare slidesLoopDesktopValue: string;
  declare slidesTrackActiveValue: boolean;
  declare slidesTrackActiveTargetValue: string;
  declare slidesStartAtValue: number;
  declare thumbsSlidesPerGroupValue: number;

  // LOCAL STATE
  private slide: Swiper;

  thumbnails() {
    // short circuit the thumbs config
    if (!this.hasThumbnailTarget) {
      return {};
    }

    const thumbsSlider = new Swiper(this.thumbnailTarget, {
      modules: [Navigation],
      slidesPerView: 'auto',
      slidesPerGroup: this.thumbsSlidesPerGroupValue || 1,
      direction: 'vertical',
      threshold: 5,
      navigation: {
        nextEl: this.hasThumbnailRightNavTarget ? this.thumbnailRightNavTarget : false,
        prevEl: this.hasThumbnailLeftNavTarget ? this.thumbnailLeftNavTarget : false
      }
    });

    return {
      swiper: thumbsSlider
    };
  }

  pagination() {
    return {
      el: this.hasPaginationTarget ? this.paginationTarget : null,
      clickable: this.hasPaginationTarget
    };
  }

  autoplay() {
    if (!this.slidesAutoPlayValue) {
      return false;
    }

    return {
      delay: 3500,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    };
  }

  navigation() {
    return {
      nextEl: this.hasRightNavTarget ? this.rightNavTarget : null,
      prevEl: this.hasLeftNavTarget ? this.leftNavTarget : null
    };
  }

  setDesktopLoopBreakpoint() {
    if (this.slidesLoopDesktopValue === 'false') {
      return false;
    }

    return this.slidesLoopValue;
  }

  breakpoints() {
    return {
      // when window width is >= 640px
      640: {
        slidesPerView: this.slidesPerViewSValue,
        slidesPerGroup: this.slidesPerGroupSValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 768px
      768: {
        slidesPerView: this.slidesPerViewMValue,
        slidesPerGroup: this.slidesPerGroupMValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1024px
      1024: {
        slidesPerView: this.slidesPerViewLValue,
        slidesPerGroup: this.slidesPerGroupLValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1280px
      1280: {
        slidesPerView: this.slidesPerViewXlValue,
        slidesPerGroup: this.slidesPerGroupXlValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1440px
      1440: {
        slidesPerView: this.slidesPerViewXxlValue,
        slidesPerGroup: this.slidesPerGroupXlValue,
        loop: this.setDesktopLoopBreakpoint()
      }
    };
  }

  mount() {
    if (!this.hasSlideTarget) {
      return null;
    }

    const config = {
      modules: [Autoplay, Navigation, Pagination, Thumbs],
      navigation: this.navigation(),
      autoHeight: this.slidesAutoHeightValue,
      slidesPerView: this.slidesPerViewXsValue,
      slidesPerGroup: 1,
      spaceBetween: this.slidesSpaceBetweenValue,
      pagination: this.pagination(),
      thumbs: this.thumbnails(),
      loop: this.slidesLoopValue,
      autoplay: this.autoplay(),
      observer: true,
      breakpoints: this.breakpoints(),
      initialSlide: this.slidesStartAtValue
    };

    this.slide = new Swiper(this.slideTarget, config);

    if (this.slidesTrackActiveValue) {
      this.slide.on('slideChange', () => {
        window.dispatchEvent(
          new CustomEvent('vulcan:hook-relay:push-event-to', {
            detail: {
              selectorOrTarget: '#product-details-live-component',
              event: 'update_live_active_slide',
              payload: { slide: this.slide.activeIndex }
            }
          })
        );
      });

      // this.slide.on('slideChange', () => {
      //   if (this.slidesTrackActiveTargetValue) {
      //     window.dispatchEvent(
      //       new CustomEvent('vulcan:hook-relay:push-event-to', {
      //         detail: {
      //           selectorOrTarget: this.slidesTrackActiveTargetValue,
      //           event: 'update_carousel_active_slide',
      //           payload: { slide: this.slide.activeIndex }
      //         }
      //       })
      //     );
      //   } else {
      //     window.dispatchEvent(
      //       new CustomEvent('vulcan:hook-relay:push-event', {
      //         detail: {
      //           event: 'update_carousel_active_slide',
      //           payload: { slide: this.slide.activeIndex }
      //         }
      //       })
      //     );
      //   }
      // });
    }

    return this.slide;
  }

  initialize() {
    this.mount();
  }
}
